import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiArrowRight, FiZap, FiTrendingUp, FiCalendar } = FiIcons;

function Demo() {
  return (
    <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Enhanced Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-gradient-to-r from-orange-400/10 to-red-400/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-orange-500/10 backdrop-blur-sm border border-orange-500/20 px-6 py-2 rounded-full text-orange-400 font-semibold mb-6">
            <SafeIcon icon={FiPlay} className="w-4 h-4" />
            <span>See It In Action</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            Let us show you how it's done
          </h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-xl text-gray-300 leading-relaxed mb-6 bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              If a picture is worth a thousand words, <span className="text-orange-400 font-semibold">a demo is worth a thousand pictures</span>. 
              That's why before you do anything, I want you to see AI in action. This demo
              gives a taster of how AI engages with customers in a natural way and bears
              the brunt of repetitive tasks, so you and your team don't have to.
            </p>
            <p className="text-lg text-gray-400">
              With AI freeing up valuable man-power and doing the work you'd "never get
              around to doing", you can unlock new levels of ROI whilst focusing on the
              things you enjoy.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-12"
        >
          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="group bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-xl font-bold text-lg flex items-center space-x-3 mx-auto shadow-2xl hover:shadow-orange-500/25 transition-all duration-300"
            >
              <SafeIcon icon={FiPlay} className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
              <span>Get Started</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </motion.button>
          </Link>
        </motion.div>

        {/* Enhanced Stats */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto"
        >
          <motion.div 
            whileHover={{ y: -10, scale: 1.05 }}
            className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300 group"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
              <SafeIcon icon={FiZap} className="w-6 h-6 text-white" />
            </div>
            <div className="text-3xl font-bold text-orange-400 mb-2">24/7</div>
            <div className="text-white font-semibold mb-1">Always Working</div>
            <div className="text-gray-400 text-sm">Never miss a lead</div>
          </motion.div>

          <motion.div 
            whileHover={{ y: -10, scale: 1.05 }}
            className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300 group"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-white" />
            </div>
            <div className="text-3xl font-bold text-orange-400 mb-2">5-6x</div>
            <div className="text-white font-semibold mb-1">ROI Increase</div>
            <div className="text-gray-400 text-sm">Proven results</div>
          </motion.div>

          <motion.div 
            whileHover={{ y: -10, scale: 1.05 }}
            className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300 group"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
              <SafeIcon icon={FiCalendar} className="w-6 h-6 text-white" />
            </div>
            <div className="text-3xl font-bold text-orange-400 mb-2">0</div>
            <div className="text-white font-semibold mb-1">Extra Work</div>
            <div className="text-gray-400 text-sm">Complete automation</div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default Demo;